/* Spotta Apparel: size picker, add to bag, slide-out bag, quantity changes, product gallery. */
(() => {
  'use strict';
  const S = window.SpottaShop || {};
  const R = Object.assign({ cart: '/cart', add: '/cart/add', change: '/cart/change' }, S.routes || {});
  const root = document.documentElement;
  const DRAWER_ID = 'spotta-cart-drawer';

  // ---------- money ----------
  function money(cents) {
    const v = (Number(cents) || 0) / 100;
    const fmt = (d, ts, ds) => {
      const [i, f] = v.toFixed(d).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, ts) + (f ? ds + f : '');
    };
    return String(S.moneyFormat || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: fmt(2, ',', '.'),
      amount_no_decimals: fmt(0, ',', '.'),
      amount_with_comma_separator: fmt(2, '.', ','),
      amount_no_decimals_with_comma_separator: fmt(0, '.', ','),
    }[k] || fmt(2, ',', '.')));
  }

  // ---------- slide-out bag ----------
  const drawer = () => document.querySelector('[data-cart-drawer]');
  let lastFocus = null;

  function openDrawer() {
    const d = drawer();
    if (!d) { window.location.href = R.cart; return; }
    lastFocus = document.activeElement;
    d.hidden = false;
    d.getBoundingClientRect();
    d.classList.add('is-open');
    root.classList.add('sp-lock');
    const panel = d.querySelector('.sp-drawer__panel');
    if (panel) panel.focus({ preventScroll: true });
  }

  function closeDrawer() {
    const d = drawer();
    if (!d || d.hidden) return;
    d.classList.remove('is-open');
    root.classList.remove('sp-lock');
    setTimeout(() => { if (!d.classList.contains('is-open')) d.hidden = true; }, 500);
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }

  function setCount(n, bump) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = n;
      const bag = el.closest('.sp-nav__bag');
      if (!bag) return;
      bag.classList.toggle('is-empty', Number(n) === 0);
      if (bump) {
        bag.classList.add('is-bumped');
        setTimeout(() => bag.classList.remove('is-bumped'), 350);
      }
    });
  }

  // Re-render the bag (and the bag page, if we're on it) with Shopify's Section Rendering API.
  async function refresh(bump) {
    const page = document.querySelector('[data-cart-page]');
    const ids = [DRAWER_ID];
    if (page && page.dataset.sectionId) ids.push(page.dataset.sectionId);
    // No Accept: application/json header here: on product and cart URLs Shopify would answer with product/cart JSON instead of sections.
    const res = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
    if (!res.ok) throw new Error('Could not refresh the bag.');
    const data = await res.json();
    if (!data || !data[DRAWER_ID]) throw new Error('Could not refresh the bag.');
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
    if (data[DRAWER_ID]) {
      const fresh = parse(data[DRAWER_ID]).querySelector('.sp-drawer__panel');
      const cur = drawer() && drawer().querySelector('.sp-drawer__panel');
      if (fresh && cur) {
        cur.innerHTML = fresh.innerHTML;
        cur.dataset.count = fresh.dataset.count;
        setCount(fresh.dataset.count, bump);
      }
    }
    if (page && data[page.dataset.sectionId]) {
      const fresh = parse(data[page.dataset.sectionId]).querySelector('[data-cart-page]');
      if (fresh) {
        page.innerHTML = fresh.innerHTML;
        page.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-in'));
        page.querySelectorAll('[data-split]').forEach((el) => el.classList.add('is-in'));
      }
    }
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener) {
      if (document.querySelector('[data-cart-page]') || !drawer()) return; // on the bag page, just follow the link
      e.preventDefault();
      openDrawer();
      return;
    }
    if (e.target.closest('[data-cart-close]')) { e.preventDefault(); closeDrawer(); }
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });

  // Quantity +/- and remove, in the drawer or on the bag page
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const line = btn.closest('[data-line]');
    if (!line) return;
    e.preventDefault();
    const box = btn.closest('[data-cart-drawer], [data-cart-page]');
    if (box) box.classList.add('is-busy');
    try {
      const res = await fetch(`${R.change}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line.dataset.line), quantity: Math.max(0, Number(btn.dataset.qtyChange)) }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not update the bag.');
      try { await refresh(false); } catch (_) { window.location.reload(); return; }
    } catch (err) {
      // Rare: e.g. not enough stock for one more. Show it under the line.
      const msg = document.createElement('p');
      msg.className = 'sp-buy__error';
      msg.setAttribute('role', 'alert');
      msg.textContent = err.message;
      line.querySelector('.sp-line__info').appendChild(msg);
      setTimeout(() => msg.remove(), 6000);
    } finally {
      if (box) box.classList.remove('is-busy');
    }
  });

  // ---------- add to bag ----------
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form.sp-buy');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[data-add-button]');
    const err = form.querySelector('[data-form-error]');
    if (!btn || btn.disabled) return;
    btn.classList.add('is-loading');
    btn.disabled = true;
    if (err) err.hidden = true;
    try {
      const res = await fetch(`${R.add}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add that to your bag.');
      // Added. If the bag can't be redrawn in place, go to the bag page instead of showing a stale bag.
      try { await refresh(true); } catch (_) { window.location.href = R.cart; return; }
      openDrawer();
    } catch (x) {
      if (err) { err.textContent = x.message; err.hidden = false; }
    } finally {
      btn.classList.remove('is-loading');
      syncButton(form);
    }
  });

  function syncButton(form, variant) {
    if (variant !== undefined) form._variant = variant;
    const v = form._variant;
    const btn = form.querySelector('[data-add-button]');
    const label = form.querySelector('[data-add-label]');
    const note = form.querySelector('[data-sold-out-note]');
    if (!btn || v === undefined) { if (btn) btn.disabled = false; return; }
    const ok = !!(v && v.available);
    btn.disabled = !ok;
    if (label) label.textContent = !v ? 'Unavailable' : ok ? (btn.dataset.labelAdd || 'Add to bag') : (btn.dataset.labelSold || 'Sold out');
    if (note) note.hidden = ok;
  }

  // ---------- product page ----------
  function initProduct(sec) {
    if (sec.dataset.ready) return;
    sec.dataset.ready = '1';
    const json = sec.querySelector('[data-product-json]');
    const form = sec.querySelector('form.sp-buy');
    if (json && form) {
      const product = JSON.parse(json.textContent);
      const idInput = form.querySelector('[data-variant-id]');
      const sets = [...form.querySelectorAll('[data-option-index]')];
      const selected = () => sets.map((fs) => { const c = fs.querySelector('input:checked'); return c ? c.value : null; });
      const find = (opts) => {
        if (!sets.length) return product.variants.find((v) => String(v.id) === idInput.value) || product.variants[0];
        return product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      };
      const priceEl = sec.querySelector('[data-price]');
      const compareEl = sec.querySelector('[data-compare]');

      const mark = () => {
        const cur = selected();
        sets.forEach((fs, i) => fs.querySelectorAll('input').forEach((inp) => {
          const opts = cur.slice();
          opts[i] = inp.value;
          const v = find(opts);
          inp.closest('.sp-pill').classList.toggle('is-unavailable', !v || !v.available);
        }));
      };

      const update = (push) => {
        const v = find(selected());
        sets.forEach((fs) => {
          const out = fs.querySelector('[data-option-value]');
          const c = fs.querySelector('input:checked');
          if (out && c) out.textContent = c.value;
        });
        if (v) {
          idInput.value = v.id;
          if (priceEl) priceEl.textContent = money(v.price);
          if (compareEl) {
            const on = v.compare_at_price && v.compare_at_price > v.price;
            compareEl.hidden = !on;
            if (on) compareEl.textContent = money(v.compare_at_price);
          }
          if (push) {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', v.id);
            window.history.replaceState({}, '', url.toString());
          }
        }
        syncButton(form, v || null);
        mark();
      };

      form.addEventListener('change', (e) => { if (e.target.closest('[data-option-index]')) update(true); });
      update(false);

      form.addEventListener('click', (e) => {
        const step = e.target.closest('[data-step]');
        if (!step) return;
        const q = form.querySelector('input[name="quantity"]');
        q.value = Math.max(1, (parseInt(q.value, 10) || 1) + Number(step.dataset.step));
      });
    }

    // Phone gallery: swipe with dots
    const track = sec.querySelector('[data-gallery-track]');
    const dots = sec.querySelector('[data-gallery-dots]');
    if (track && dots) {
      const shots = [...track.children];
      const buttons = [...dots.children];
      buttons.forEach((b, i) => b.addEventListener('click', () => track.scrollTo({ left: shots[i].offsetLeft - track.offsetLeft, behavior: 'smooth' })));
      track.addEventListener('scroll', () => {
        const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        buttons.forEach((b, j) => b.classList.toggle('is-active', i === j));
      }, { passive: true });
    }
  }

  const init = (scope) => (scope || document).querySelectorAll('[data-product-section]').forEach(initProduct);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(false).catch(() => {}); });
})();
